import { useMutation } from '@tanstack/react-query';
import { useTolgee } from '@tolgee/react';
import { Select } from 'antd';
import mixpanel from 'mixpanel-browser';
import { useCallback } from 'react';

import { SelectProps } from '../components/_atoms/_Select';
import useAccessBadge from '../hooks/useAccessBadge';
import { getLang, storePreferredLocale } from '../hooks/useLocale';
import { localeOptions } from '../providers/tolgee';
import backendService from '../services/backendService';

const groupedOptions = Object.values(
  localeOptions.reduce((acc, [value, displayName]) => {
    const key = value.slice(0, 2);
    if (!acc[key]) {
      acc[key] = { label: displayName, value };
    } else if ('options' in acc[key]) {
      (acc[key] as { options: { label: string; value: string }[] }).options.push({
        label: displayName,
        value,
      });
    } else {
      acc[key] = {
        label: displayName.slice(4), // hopefully all future flag emojis are 4 characters
        options: [
          acc[key] as { label: string; value: string },
          { label: displayName, value },
        ],
      };
    }
    return acc;
  }, {} as Record<string, { label: string; value: string } | { label: string; options: { label: string; value: string }[] }>),
);

export const LangSelector: React.FC<{
  selectProps?: Pick<SelectProps, 'label' | 'labelStyle'>;
}> = ({ selectProps }) => {
  const tolgee = useTolgee();
  const { accessBadge, isFetching, refetchAccessBadge } = useAccessBadge();
  const { mutate, isLoading: isMutating } = useMutation(
    ({ locale }: { locale: string }) =>
      backendService.instance.users.editProfile({
        preferredLocale: locale,
      }),
    {
      onSuccess: (result, { locale }) => {
        refetchAccessBadge();
        storePreferredLocale(result.preferredLocale ?? locale);
      },
    },
  );

  const onChange = useCallback(
    (locale: string) => {
      if (accessBadge?.user?.id) {
        mutate({ locale });
        storePreferredLocale(locale);
        tolgee.changeLanguage(locale);
      } else {
        storePreferredLocale(locale);
        tolgee.changeLanguage(locale);
      }
      mixpanel.track('locale changed', {
        selectedLocale: locale,
        detectedLocale: getLang(),
      });
    },
    [accessBadge, mutate, tolgee],
  );

  const currentLanguage = tolgee.getLanguage();

  return (
    <div
    // style={{
    //   position: 'fixed',
    //   bottom: '20px',
    //   right: '80px',
    //   zIndex: 10001,
    // }}
    >
      <Select
        {...selectProps}
        options={groupedOptions}
        onSelect={(option: string) => {
          onChange(option);
        }}
        loading={!currentLanguage || isMutating || isFetching}
        // placement="topRight"
        value={localeOptions.find(([value]) => value === currentLanguage)?.[1]}
      />
    </div>
  );
};
